// ─────────────────────────────────────────────────────────────────────────
// userNames — resolve audit `updatedBy` ids strictly from MongoDB users.
//
// Rate-history entries store the actor as an id. User identity for this view
// must come from the MongoDB `users` module, not the legacy MySQL user-detail
// endpoint. The Mongo row can expose the same person through legacyUserId,
// userId, _id, or id, so all of those identities are indexed.
// ─────────────────────────────────────────────────────────────────────────

import { fetchJsonWithAuth } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';

export function userDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  const firstName = user.firstName ?? user.first_name ?? user.FIRST_NAME;
  const lastName = user.lastName ?? user.last_name ?? user.LAST_NAME;
  return [firstName, lastName].filter(Boolean).join(' ').trim()
    || user.name
    || user.userName
    || user.user_name
    || user.username
    || user.email
    || '';
}

function idText(value) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'object') {
    return String(value.$oid ?? value.id ?? value._id ?? '').trim();
  }
  return String(value).trim();
}

function userIds(user = {}) {
  return [
    user.legacyUserId,
    user.userId,
    user.user_id,
    user._id,
    user.id,
  ].map(idText).filter(Boolean);
}

export function indexUserRows(rows) {
  const map = {};
  (Array.isArray(rows) ? rows : []).forEach((user) => {
    const name = userDisplayName(user);
    if (!name) return;
    userIds(user).forEach((key) => {
      if (map[key] === undefined) map[key] = name;
    });
  });
  return map;
}

// One raw Mongo users-directory request is shared by every unresolved actor.
// IMPORTANT: do not replace this with /get-detailed-view; that endpoint is the
// legacy user-detail path and can resolve an id against MySQL instead of the
// MongoDB users collection used by Zinnext.
let mongoUsersPromise;
function getMongoUsers() {
  if (!mongoUsersPromise) {
    mongoUsersPromise = fetchJsonWithAuth(AUTH_URL, '/module/list?module=users')
      .then((res) => {
        const value = res?.data?.data ?? res?.data ?? [];
        return Array.isArray(value) ? value : [];
      })
      .catch(() => []);
  }
  return mongoUsersPromise;
}

// Session cache: actor id → Promise<string>.
const userNameCache = new Map();

/**
 * Resolve an audit actor from MongoDB users only.
 * Prefer legacyUserId/userId matches because rate-history `updatedBy` is
 * normally an auth/legacy numeric id. ObjectId/id matching is retained for
 * records stamped by newer flows.
 */
export function fetchUserName(userId) {
  const key = idText(userId);
  if (!key) return Promise.resolve('');
  if (userNameCache.has(key)) return userNameCache.get(key);

  const request = getMongoUsers().then((rows) => {
    const exact = rows.find((user) => (
      idText(user?.legacyUserId) === key
      || idText(user?.userId) === key
      || idText(user?.user_id) === key
    ));
    if (exact) return userDisplayName(exact);

    const objectMatch = rows.find((user) => (
      idText(user?._id) === key || idText(user?.id) === key
    ));
    return userDisplayName(objectMatch);
  }).catch(() => '');

  userNameCache.set(key, request);
  return request;
}

export function primeUserName(userId, name) {
  userNameCache.set(String(userId), Promise.resolve(name));
}

export function clearUserNameCache() {
  userNameCache.clear();
  mongoUsersPromise = undefined;
}
